/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import React, { useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import ReactMultiSelectCheckboxes from 'react-multiselect-checkboxes';
import { Link } from 'react-router-dom';
import { IOrderHistoryLine } from '../../../models/order';
import '../../common/react-multiselect-checkboxes.css';
import HeaderFilterInput from '../order-history/header-filter-input';
import '../order-details/order-details-styles.css';
import '../order-history/order-history.css';
import { ISession } from '../../../models/pro-sites';
import { isMiracleEar } from '../../../hooks/miracle-ear-hook';

interface IOrderHistoryListProps {
    orders: IOrderHistoryLine[];
    session: ISession;
}

export const OrderHistoryList = ({
    orders,
    session,
}: IOrderHistoryListProps) => {
    const intl = useIntl();
    const [visibleFilters, setVisibleFilters] = useState<boolean>(true);
    const orderMessaging = intl.formatMessage({
        id: 'New_orders_may_not_display_immediately',
        defaultMessage: 'New orders may not display immediately',
    });
    const filterCaseInsensitiveAndContains = (filter: any, row: any) => {
        const id = filter.pivotId || filter.id;
        return row[id] === null
            ? false
            : row[id] !== undefined
            ? String(row[id].toLowerCase()).includes(filter.value.toLowerCase())
            : true;
    };

    const filterDateTime = (filter: any, row: any) => {
        const id = filter.pivotId || filter.id;
        return row[id] === null
            ? false
            : row[id] !== undefined
            ? String(
                  moment(row[id]).format('MMM DD, YYYY').toLowerCase()
              ).includes(filter.value.toLowerCase())
            : true;
    };

    const orderTypes = Array.from(
        new Set(orders.map((order) => order.orderType))
    ).map((orderType) => {
        return {
            label: orderType,
            value: orders.find((o) => o.orderType === orderType)!.orderType,
        };
    });
    const orderStatuses = Array.from(
        new Set(orders.map((order) => order.orderStatus))
    ).map((orderStatus) => {
        return {
            label: orderStatus,
            value: orders.find((o) => o.orderStatus === orderStatus)!
                .orderStatus,
        };
    });
    /*
    const orderStatuses = [
        intl.formatMessage({
            id: 'Select_filter'
        })
    ].concat(
        Array.from(
            new Set(orders.map((order: IOrderHistoryLine) => order.orderStatus))
        )
    );
*/
    const toggleFilter = () => {
        setVisibleFilters(!visibleFilters);
    };

    return (
        <ReactTable
            data={orders}
            columns={[
                {
                    Header: () => {
                        if (session.isAccountThirdParty) {
                            return intl.formatMessage({ id: 'Ship_To' });
                        }
                        return intl.formatMessage({ id: 'Bill_To' });
                    },
                    id: session.isAccountThirdParty ? 'Ship_To' : 'Bill_To',
                    minWidth: 190,
                    maxWidth: 300,
                    accessor: (d) =>
                        session.isAccountThirdParty
                            ? d.shipToAccount + ' - ' + d.shipToAccountName
                            : d.billToAccount + ' - ' + d.billToAccountName,

                    Cell: (c) =>
                        session.isAccountThirdParty ? (
                            <span
                                id={'shipToAccount' + c.original.shipToAccount}
                            >
                                {c.original.shipToAccount +
                                    ' - ' +
                                    c.original.shipToAccountName}
                            </span>
                        ) : (
                            <span
                                id={'billToAccount' + c.original.billToAccount}
                            >
                                {c.original.billToAccount +
                                    ' - ' +
                                    c.original.billToAccountName}
                            </span>
                        ),
                    Filter: ({ filter, onChange }) => (
                        <HeaderFilterInput
                            filter={filter}
                            onChange={onChange}
                        />
                    ),
                },
                {
                    Header: intl.formatMessage({ id: 'Order_#' }),
                    id: 'orderNumber',
                    minWidth: 70,
                    maxWidth: 80,
                    accessor: (d) => d.orderNumber,
                    Cell: (c) =>
                        c.original.orderNumber === 'Unavailable' ? (
                            <span>Unavailable</span>
                        ) : (
                            <span id={'orderNumber' + c.original.orderNumber}>
                                <Link
                                    to={{
                                        pathname: '/my-account/order-details',
                                        state: {
                                            orderNumber: c.original.orderNumber,
                                        },
                                    }}
                                >
                                    {c.original.orderNumber}
                                </Link>
                            </span>
                        ),
                    Filter: ({ filter, onChange }) => (
                        <HeaderFilterInput
                            filter={filter}
                            onChange={onChange}
                        />
                    ),
                },
                {
                    Header: intl.formatMessage({ id: 'Order_Placed' }),
                    id: 'orderPlaced',
                    minWidth: 70,
                    maxWidth: 110,
                    accessor: (d) => d.received,
                    Cell: (c) => (
                        <span id={c.original.received}>
                            {c.original.received &&
                                moment(c.original.received).format(
                                    'MMM DD, YYYY'
                                )}
                        </span>
                    ),
                    filterMethod: filterDateTime,
                    Filter: ({ filter, onChange }) => (
                        <HeaderFilterInput
                            filter={filter}
                            onChange={onChange}
                        />
                    ),
                },
                {
                    Header: intl.formatMessage({ id: 'Type' }),
                    id: 'orderType',
                    maxWidth: 130,
                    accessor: (d) => d.orderType,
                    Cell: (c) => (
                        <span
                            id={
                                'orderType' + c.original.orderType.split(' ')[0]
                            }
                        >
                            {c.original.orderType}
                        </span>
                    ),
                    filterMethod: (
                        filter: { value: any[]; id: string | number },
                        row: { [x: string]: any }
                    ) => {
                        return (
                            filter.value.length === 0 ||
                            filter.value.find((x) => x.value === row[filter.id])
                        );
                    },
                    Filter: ({ onChange }) => (
                        <ReactMultiSelectCheckboxes
                            placeholderButtonLabel={intl.formatMessage({
                                id: 'Select_filter',
                            })}
                            onChange={onChange}
                            options={orderTypes}
                        />
                    ),
                },
                {
                    Header: isMiracleEar(session.siteNumber)
                        ? intl.formatMessage({
                              id: 'Patient_Reference_#',
                              defaultMessage: 'Patient Reference #',
                          })
                        : intl.formatMessage({ id: 'Patient' }),
                    id: 'patientName',
                    minWidth: 140,
                    maxWidth: 170,
                    accessor: (d) => d.patientName,
                    Cell: (c) => (
                        <span id={'patientName' + c.original.patientName}>
                            {c.original.patientName}
                        </span>
                    ),
                    Filter: ({ filter, onChange }) => (
                        <HeaderFilterInput
                            filter={filter}
                            onChange={onChange}
                        />
                    ),
                },

                {
                    Header: intl.formatMessage({ id: 'Status' }),
                    id: 'orderStatus',
                    minWidth: 70,
                    maxWidth: 170,
                    accessor: (d) => d.orderStatus,
                    filterMethod: (
                        filter: { value: any[]; id: string | number },
                        row: { [x: string]: any }
                    ) => {
                        return (
                            filter.value.length === 0 ||
                            filter.value.find((x) => x.value === row[filter.id])
                        );
                    },
                    Filter: ({ onChange }) => (
                        <ReactMultiSelectCheckboxes
                            placeholderButtonLabel={intl.formatMessage({
                                id: 'Select_filter',
                            })}
                            onChange={onChange}
                            options={orderStatuses}
                        />
                    ),

                    Cell: (c) => (
                        <span id="orderStatusBtn">
                            {c.original.shipped ? (
                                <>
                                    {c.original.orderStatus && (
                                        <span style={{ paddingRight: '10px' }}>
                                            {c.original.orderStatus}
                                        </span>
                                    )}

                                    {c.original.trackingUrl ? (
                                        <a
                                            href={c.original.trackingUrl}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            Track Order
                                            <span
                                                title={intl.formatMessage({
                                                    id: 'Track_Shipment',
                                                })}
                                                className="fas fa-external-link-alt"
                                                style={{
                                                    paddingLeft: '10px',
                                                }}
                                            />
                                        </a>
                                    ) : (
                                        <>
                                            {/** 
                                            {c.original.orderStatus && (
                                                <span
                                                    style={{
                                                        paddingRight: '10px'
                                                    }}
                                                >
                                                    {c.original.orderStatus}
                                                </span>
                                                )}
                                                */}
                                            <Link
                                                className="btn-sm btn-primary"
                                                to={{
                                                    pathname:
                                                        '/my-account/order-details',
                                                    state: {
                                                        orderNumber:
                                                            c.original
                                                                .orderNumber,
                                                    },
                                                }}
                                            >
                                                <FormattedMessage
                                                    id="See_Details"
                                                    defaultMessage="See Details"
                                                />
                                            </Link>
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    {c.original.orderStatus && (
                                        <span style={{ paddingRight: '10px' }}>
                                            {c.original.orderStatus}
                                        </span>
                                    )}
                                    {c.original.orderNumber !==
                                        'Unavailable' && (
                                        <Link
                                            className="btn-sm btn-primary"
                                            to={{
                                                pathname:
                                                    '/my-account/order-details',
                                                state: {
                                                    orderNumber:
                                                        c.original.orderNumber,
                                                },
                                            }}
                                        >
                                            <FormattedMessage
                                                id="See_Details"
                                                defaultMessage="See Details"
                                            />
                                        </Link>
                                    )}
                                </>
                            )}
                        </span>
                    ),
                },
            ]}
            getTheadFilterProps={() => {
                return {
                    style:
                        visibleFilters === false ? { display: 'none' } : null,
                };
            }}
            getTheadFilterThProps={() => {
                return {
                    style: {
                        textAlign: 'left',
                        position: 'inherit',
                        overflow: 'inherit',
                    },
                };
            }}
            getTheadThProps={() => {
                return {
                    style: { textAlign: 'left' },
                };
            }}
            defaultPageSize={50}
            className="-striped -highlight"
            filterable={true}
            defaultFilterMethod={filterCaseInsensitiveAndContains}
            minRows={0}
            NoDataComponent={() => null}
            previousText={intl.formatMessage({ id: 'Previous' })}
            nextText={intl.formatMessage({ id: 'Next' })}
            rowsText={intl.formatMessage({ id: 'rows' })}
            pageText={intl.formatMessage({ id: 'Page' })}
        >
            {(state, makeTable) => {
                let recordsInfoText = '';
                let filtersText = '';

                const {
                    filtered,
                    pageRows,
                    pageSize = 0,
                    sortedData,
                    page = 0,
                } = state;

                if (sortedData && sortedData.length > 0) {
                    const isFiltered = filtered.length > 0;
                    const totalRecords = sortedData.length;
                    const recordsCountFrom = page * pageSize + 1;
                    const recordsCountTo =
                        recordsCountFrom + pageRows.length - 1;
                    const showing = intl.formatMessage({ id: 'Showing' });
                    const of = intl.formatMessage({ id: 'of' });
                    const filteredOrders = intl.formatMessage({
                        id: 'filtered_orders',
                    });

                    const orders = intl
                        .formatMessage({ id: 'Orders' })
                        .toLocaleLowerCase();

                    if (isFiltered) {
                        recordsInfoText = `${showing} ${recordsCountFrom}-${recordsCountTo} ${of} ${totalRecords} ${filteredOrders}`;
                    } else {
                        recordsInfoText = `${showing} ${recordsCountFrom}-${recordsCountTo} ${of} ${totalRecords} ${orders}`;
                    }
                } else {
                    recordsInfoText = intl.formatMessage({ id: 'No_orders' });
                }

                if (visibleFilters === false) {
                    filtersText = intl.formatMessage({ id: 'Show_filters' });
                } else {
                    filtersText = intl.formatMessage({ id: 'Hide_filters' });
                }

                return (
                    <div className="main-grid">
                        <div className="above-table text-left">
                            <div
                                className="col-sm-12"
                                style={{ padding: '0px 0px 10px 0px' }}
                            >
                                <span className="records-info">
                                    {recordsInfoText}
                                    <span className="padding-order">
                                        {orderMessaging}
                                    </span>
                                </span>
                                <span className="pull-right">
                                    <a
                                        style={{ cursor: 'pointer' }}
                                        id="showHideFilters"
                                        onClick={() => {
                                            toggleFilter();
                                        }}
                                    >
                                        <span className="fa fa-filter" />{' '}
                                        {filtersText}
                                    </a>
                                </span>
                            </div>
                        </div>
                        {makeTable()}
                    </div>
                );
            }}
        </ReactTable>
    );
};

export default OrderHistoryList;
